import { useState, useEffect, useRef } from 'react';
import MarkdownEditor from '../shared/MarkdownEditor';
import Dropdown from '../shared/Dropdown';
import EditSaveButtons from '../shared/EditSaveButtons';
import { adminApi } from '../../lib/admin-api';

export default function DetailsTab({ configuration, onUpdate, onRefreshConfiguration, availableJobs, availableConnections, availableProviders, availableModels }) {
  const [name, setName] = useState(configuration?.name || '');
  const [dimensions, setDimensions] = useState(configuration?.dimensions || null);
  const [selectedConnectionId, setSelectedConnectionId] = useState(configuration?.connection?.id || null);
  const [selectedModelId, setSelectedModelId] = useState(configuration?.model_id || null);
  const [isSaving, setIsSaving] = useState(false);
  const [isCreatingConnection, setIsCreatingConnection] = useState(false);
  const [newApiKey, setNewApiKey] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);

  // Track newly created connection ID to preserve selection after refresh
  const newlyCreatedConnectionIdRef = useRef(null);

  // Filter connections by provider to only show compatible connections
  const filteredConnections = availableConnections.filter(
    conn => conn.provider_id === configuration?.provider_id
  );

  // Filter models by provider to only show compatible models
  const filteredModels = availableModels.filter(
    model => model.provider === configuration?.provider
  );

  useEffect(() => {
    setName(configuration?.name || '');
    setDimensions(configuration?.dimensions || null);

    // If we just created a connection, preserve that selection instead of resetting
    if (newlyCreatedConnectionIdRef.current) {
      setSelectedConnectionId(newlyCreatedConnectionIdRef.current);
      newlyCreatedConnectionIdRef.current = null; // Clear it after using
    } else {
      setSelectedConnectionId(configuration?.connection?.id || null);
    }

    setSelectedModelId(configuration?.model_id || null);
  }, [configuration]);

  const handleCancel = () => {
    // Reset all values to original configuration
    setName(configuration?.name || '');
    setDimensions(configuration?.dimensions || null);
    setSelectedConnectionId(configuration?.connection?.id || null);
    setSelectedModelId(configuration?.model_id || null);
    setIsCreatingConnection(false);
    setNewApiKey('');
    setIsEditMode(false);
  };

  const handleCancelCreateConnection = () => {
    setIsCreatingConnection(false);
    setNewApiKey('');
  };

  const handleSubmitNewConnection = async () => {
    if (!newApiKey.trim()) {
      alert('Please enter an API key');
      return;
    }

    setIsSaving(true);
    try {
      // Create the connection
      const response = await adminApi.modelConfigurations.createConnection(configuration.id, { api_key: newApiKey });

      // Store the new connection ID in ref so useEffect preserves it after refresh
      newlyCreatedConnectionIdRef.current = response.connection.id;

      // Reset creation state
      setIsCreatingConnection(false);
      setNewApiKey('');

      // Refresh overview to get the new connection in the dropdown
      // The useEffect will run and use the ref to select the new connection
      await onRefreshConfiguration?.();

      // Enter edit mode to show there are unsaved changes
      setIsEditMode(true);
    } catch (error) {
      console.error('Failed to create connection:', error);
      alert('Failed to create connection');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      alert('Please enter a configuration name');
      return;
    }

    setIsSaving(true);
    try {
      const updates = {
        name,
        dimensions,
        connection_id: selectedConnectionId,
        model_id: selectedModelId
      };

      await onUpdate?.(updates);
      setIsEditMode(false); // Exit edit mode after successful save
    } catch (error) {
      console.error('Failed to save configuration:', error);
      alert('Failed to save configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const isEmbeddingModel = configuration?.model_type === 'embedding';

  return (
    <div className="p-8 max-w-5xl">
      {/* Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white mb-2">Details</h2>
          <p className="text-sm text-gray-400">Manage model parameters and settings</p>
        </div>

        {/* Edit/Save/Cancel Buttons */}
        <EditSaveButtons
          isEditMode={isEditMode}
          isSaving={isSaving}
          onEdit={() => setIsEditMode(true)}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      </div>

      <div className="space-y-8">
        {/* Configuration Name */}
        <div className="space-y-2">
          <label className="block text-sm font-semibold text-gray-200">
            Configuration Name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={!isEditMode}
            placeholder="Enter configuration name..."
            className="w-full h-11 bg-gray-800/50 border border-gray-700/50 rounded-lg px-4 text-white text-sm focus:outline-none focus:border-purple-500/50 focus:bg-gray-800 transition-all placeholder:text-gray-600 disabled:opacity-50"
          />
        </div>

        <div className="h-px bg-gradient-to-r from-transparent via-gray-700/50 to-transparent"></div>

        {/* Model Selector */}
        <div className="space-y-2">
          <label className="block text-sm font-semibold text-gray-200">
            Model
          </label>
          <Dropdown
            value={selectedModelId || ''}
            onChange={(e) => setSelectedModelId(e.target.value || null)}
            options={[
              { value: '', label: 'Select a model' },
              ...filteredModels.map(model => ({
                value: model.id,
                label: `${model.name} (${model.model_identifier})`
              }))
            ]}
            disabled={!isEditMode}
            className="w-full h-11 bg-gray-800/50 border-gray-700/50 focus:border-purple-500/50 focus:bg-gray-800"
          />
        </div>

        <div className="h-px bg-gradient-to-r from-transparent via-gray-700/50 to-transparent"></div>

        {/* Connection Selector */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="block text-sm font-semibold text-gray-200">
              {isCreatingConnection ? 'Enter API Key' : 'Connection'}
            </label>
            {!isCreatingConnection ? (
              <button
                onClick={() => setIsCreatingConnection(true)}
                disabled={!isEditMode}
                className="text-xs px-3 py-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 rounded-md transition-colors flex items-center gap-1"
                style={{ color: 'var(--color-text-on-accent)' }}
                type="button"
              >
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                New Connection
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={handleCancelCreateConnection}
                  disabled={isSaving}
                  className="text-xs px-3 py-1 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-700 rounded-md transition-colors"
                  style={{ color: 'var(--color-text-on-accent)' }}
                  type="button"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitNewConnection}
                  disabled={isSaving || !newApiKey.trim()}
                  className="text-xs px-3 py-1 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 rounded-md transition-colors"
                  style={{ color: 'var(--color-text-on-accent)' }}
                  type="button"
                >
                  {isSaving ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            )}
          </div>
          {!isCreatingConnection ? (
            <Dropdown
              value={selectedConnectionId || ''}
              onChange={(e) => setSelectedConnectionId(e.target.value || null)}
              options={[
                { value: '', label: 'Select a connection' },
                ...filteredConnections.map(conn => ({
                  value: conn.id,
                  label: `${conn.status === 'active' ? '🟢' : '🔴'} ${conn.name}`
                }))
              ]}
              disabled={!isEditMode}
              className="w-full h-11 bg-gray-800/50 border-gray-700/50 focus:border-purple-500/50 focus:bg-gray-800"
            />
          ) : (
            <input
              type="password"
              value={newApiKey}
              onChange={(e) => setNewApiKey(e.target.value)}
              placeholder="sk-..."
              className="w-full h-11 bg-gray-800/50 border border-gray-700/50 rounded-lg px-4 text-white text-sm focus:outline-none focus:border-purple-500/50 focus:bg-gray-800 transition-all placeholder:text-gray-600"
            />
          )}
        </div>

        {/* Dimensions (for embedding models) */}
        {isEmbeddingModel && (
          <>
            <div className="h-px bg-gradient-to-r from-transparent via-gray-700/50 to-transparent"></div>

            <div className="space-y-3">
              <div className="flex items-baseline justify-between">
                <label className="block text-sm font-semibold text-gray-200">
                  Dimensions
                </label>
                <span className="text-xs text-gray-500">Embedding vector dimensions</span>
              </div>
              <input
                type="number"
                min="1"
                max="10000"
                value={dimensions || ''}
                onChange={(e) => setDimensions(parseInt(e.target.value) || null)}
                disabled={!isEditMode}
                placeholder="Leave empty for model default"
                className="w-full h-11 bg-gray-800/50 border border-gray-700/50 rounded-lg px-4 text-white text-sm focus:outline-none focus:border-purple-500/50 focus:bg-gray-800 transition-all placeholder:text-gray-600 disabled:opacity-50"
              />
            </div>
          </>
        )}

      </div>
    </div>
  );
}
